/* eslint-disable react-hooks/exhaustive-deps */
import { AddIcon, CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, FormControl, FormLabel, Input, Radio, RadioGroup, Stack, Tag, Text, useToast } from '@chakra-ui/react'
import { useMutation } from '@apollo/client'
import React, { FormEvent, useEffect, useState, useRef } from 'react'
import { MUTATION_SET_HERO_PHOTO } from '../../queries/queries'
import { keyValue, selectIDInterface } from '../../lib/types'
import { filterObject, SelectOptionsInterface, Seperator, SnapSelectBox, SystemFormHeading, uploadFile } from '../../pages/systemForm.support'
import CryptoJS from 'crypto-js'
import { dragMovementInterface, SegmentBox, swapArrays } from './multiFileUpload.support'
import { addButtonStyles, closeButtonStyles, tagStyles } from './multiFileUpload.style-overrides'
import { CloudLink } from './multiFileUpload.sub.cloudlink'
import { FileViewTypeBackEndKey, FileViewTypeEnum } from '../../commons/common'
import { getEnvironmentURL } from '../../commons/env'

export interface fileInterface {
  product_doc_id?: string | number
  file?: string | Blob | null
  fileType: selectIDInterface
  file_id?: string | null
  md5?: string
  cloudLink?: string | object | Response | null | undefined
  isPrivateFile: Boolean | false
  is_hero?: boolean
  order: number
  errorMessage?: string
  fileViewType?: FileViewTypeEnum.PRIVATE | FileViewTypeEnum.PUBLIC
}
export const MultiFileUpload = ({
  onChangeCallBack,
  options,
  defaultData
}: {
  options: SelectOptionsInterface[]
  onChangeCallBack: (response: fileInterface[]) => void
  defaultData: fileInterface[]
}): JSX.Element => {
  const onChangeCallBackRef = useRef(onChangeCallBack)
  const previousDefaultDataRef = useRef<fileInterface[]>([])
  const previousFileListRef = useRef<fileInterface[]>([])
  const isSyncingFromDefaultDataRef = useRef<boolean>(false)

  const areFileListsEqual = (listA: fileInterface[], listB: fileInterface[]): boolean => {
    if (listA.length !== listB.length) {
      return false
    }

    return listA.every((item, index) => {
      const comparator = listB[index]
      if (!comparator) {
        return false
      }

      const isFileTypeEqual = item.fileType?.id === comparator.fileType?.id

      return (
        item.product_doc_id === comparator.product_doc_id &&
        item.file === comparator.file &&
        isFileTypeEqual &&
        item.file_id === comparator.file_id &&
        item.md5 === comparator.md5 &&
        item.cloudLink === comparator.cloudLink &&
        item.isPrivateFile === comparator.isPrivateFile &&
        item.order === comparator.order &&
        item.errorMessage === comparator.errorMessage &&
        item.fileViewType === comparator.fileViewType
      )
    })
  }

  // Update the ref when the callback changes
  useEffect(() => {
    onChangeCallBackRef.current = onChangeCallBack
  }, [onChangeCallBack])

  useEffect(() => {
    if (!Array.isArray(defaultData)) {
      return
    }

    if (areFileListsEqual(previousDefaultDataRef.current, defaultData)) {
      return
    }

    const clonedDefaultData = defaultData.map(fileItem => ({ ...fileItem }))
    previousDefaultDataRef.current = clonedDefaultData
    previousFileListRef.current = clonedDefaultData
    isSyncingFromDefaultDataRef.current = true
    setFileList(clonedDefaultData)
  }, [defaultData])

  const toast = useToast()
  const [fileList, setFileList] = useState<fileInterface[]>([])
  const [setHeroPhoto] = useMutation(MUTATION_SET_HERO_PHOTO)
  const [, setCurrentMd5] = useState<string>('')

  interface fileURLInterface {
    url: string
    cdn_url: string
    file_id?: string
    isPrivate: Boolean
    eTag: string
    fName: string
    messages?: {
      code: string
      message: string
    }
    response?: {
      errMessage?: string
    }
  }

  const getcloudLink = async (fileInterfaceObject: fileInterface): Promise<fileInterface> => {
    if (fileInterfaceObject.file !== null && fileInterfaceObject.file !== undefined && fileInterfaceObject.fileType !== null) {
      try {
        const formData = new FormData()
        formData.append(FileViewTypeBackEndKey, fileInterfaceObject.fileViewType ?? FileViewTypeEnum.PRIVATE)
        formData.append('doc', fileInterfaceObject.file as Blob)
        const fileURL = (await uploadFile(formData)) as fileURLInterface

        if (fileURL === null) {
          const errorMessage = 'Unable to reach the upload service. Please check your connection and try again.'
          toast({
            title: 'Upload failed',
            description: errorMessage,
            status: 'error',
            duration: 6000,
            isClosable: true
          })

          return {
            ...fileInterfaceObject,
            cloudLink: 'Error',
            errorMessage,
            file_id: null
          }
        }

        if (fileURL?.messages?.message === 'Error') {
          const errorMessage = fileURL?.response?.errMessage ?? 'File upload failed.'
          toast({
            title: 'Upload failed',
            description: errorMessage,
            status: 'error',
            duration: 6000,
            isClosable: true
          })

          return {
            ...fileInterfaceObject,
            cloudLink: 'Error',
            errorMessage
          }
        }

        if (typeof fileURL?.cdn_url !== 'string' || fileURL?.cdn_url.length === 0) {
          const errorMessage = 'Upload response was invalid. Please try again.'
          toast({
            title: 'Upload failed',
            description: errorMessage,
            status: 'error',
            duration: 6000,
            isClosable: true
          })

          return {
            ...fileInterfaceObject,
            cloudLink: 'Error',
            errorMessage,
            file_id: null
          }
        }

        return {
          ...fileInterfaceObject,
          cloudLink: fileURL?.cdn_url ?? null,
          file_id: fileURL?.file_id ?? null
        }
      } catch (error) {
        const parsedError = error instanceof Error ? error.message : 'An unexpected error occurred during file upload.'
        toast({
          title: 'Upload failed',
          description: parsedError,
          status: 'error',
          duration: 6000,
          isClosable: true
        })

        return {
          ...fileInterfaceObject,
          cloudLink: 'Error',
          errorMessage: parsedError,
          file_id: null
        }
      }
    }

    return fileInterfaceObject
  }

  const performFileUpload = async (data: fileInterface[]): Promise<void> => {
    const filesObject = await data.reduce(async (reducer: Promise<fileInterface[]>, item: fileInterface) => {
      const cloudUploadResult = await getcloudLink(item)

      return [...(await reducer), { ...cloudUploadResult }]
    }, Promise.resolve([]))

    setFileList(filesObject)
  }

  const handleChange = (event: FormEvent, index: number): void => {
    const HTMLFileElement = event.target as HTMLInputElement
    const currentFile = HTMLFileElement.files ? HTMLFileElement.files[0] : null
    const initialFileList = [...fileList]
    const reader = new FileReader()
    if (currentFile !== null && reader !== null) {
      reader.readAsBinaryString(currentFile)

      reader.onload = () => {
        const rawMd5 = CryptoJS.MD5(reader.result as string).toString()
        setCurrentMd5(rawMd5.toString())
        const reducedFiles = fileList.reduce((reducer: fileInterface[], item, arrayIndex) => {
          if (arrayIndex === index) {
            const results = filterObject(initialFileList as unknown as keyValue[], 'md5', rawMd5)
            if (results?.length === 0) {
              return [
                ...reducer,
                {
                  ...item,
                  file: currentFile,
                  md5: rawMd5.toString(),
                  cloudLink: 'loading',
                  file_id: null
                }
              ]
            }

            // Show error toast if the same file is uploaded
            toast({
              title: 'Error',
              description: `File already uploaded.`,
              status: 'error',
              duration: 6000,
              isClosable: true
            })
            return [...reducer, { ...item, file: null, md5: '' }]
          }

          return [...reducer, { ...item }]
        }, [])
        setFileList(reducedFiles)
        if (Array.isArray(reducedFiles) && reducedFiles.length > 0) {
          void performFileUpload(reducedFiles)
        }
      }
    }
  }
  useEffect(() => {
    if (areFileListsEqual(previousFileListRef.current, fileList)) {
      return
    }

    if (isSyncingFromDefaultDataRef.current) {
      isSyncingFromDefaultDataRef.current = false
      previousFileListRef.current = fileList
      return
    }

    previousFileListRef.current = fileList
    onChangeCallBackRef.current(fileList)
  }, [fileList])
  const defaultFileType = '1'

  const [dragEnterElement, setDragEnterElement] = useState<null | number>(null)

  const updateFileListOrder = (array: fileInterface[]): void => {
    const updatedOrder = array.reduce((reducer: fileInterface[], item, index) => [...reducer, { ...item, order: index }], [])
    setFileList(updatedOrder)
  }

  const deleteFileFromCloud = async (fileId: string): Promise<boolean> => {
    try {
      const deleteFileURL = getEnvironmentURL.fileDelete(fileId)
      const response = await fetch(deleteFileURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        console.log('File deleted successfully from cloud storage')
        return true
      } else {
        console.error('Failed to delete file from cloud storage')
        return false
      }
    } catch (error) {
      console.error('Error deleting file from cloud storage:', error)
      return false
    }
  }

  const handleFileDelete = async (index: number): Promise<void> => {
    const fileToDelete = fileList[index]

    // Show confirmation dialog before deleting
    const confirmDelete = window.confirm('Are you sure you want to delete this file?')

    if (confirmDelete) {
      // If file has been uploaded to cloud storage (has file_id), delete it first
      if (fileToDelete.file_id) {
        const cloudDeleteSuccess = await deleteFileFromCloud(fileToDelete.file_id)
        if (!cloudDeleteSuccess) {
          // Show error toast if cloud deletion fails
          toast({
            title: 'Warning',
            description: 'File could not be deleted from cloud storage, but was removed from the list.',
            status: 'warning',
            duration: 6000,
            isClosable: true
          })
        }
      }

      // Remove file from local state regardless of cloud deletion result
      const updatedFileList = fileList.reduce((reducer: fileInterface[], item, innerIndex) => {
        if (innerIndex === index) {
          return reducer
        }
        return [...reducer, item]
      }, [])

      updateFileListOrder(updatedFileList)
    }
  }

  const handleFileTypeChange = async (index: number, selectValue: FileViewTypeEnum.PRIVATE | FileViewTypeEnum.PUBLIC): Promise<void> => {
    const currentFile = fileList[index]

    // Only show confirmation when file_id is available (existing file)
    if (currentFile.file_id) {
      const confirmChange = window.confirm('Are you sure you want to change the file visibility?')
      if (!confirmChange) {
        return
      }

      // Make API call to move file if user confirms and file_id exists
      try {
        const moveFileURL = getEnvironmentURL.fileMove(currentFile.file_id)
        const response = await fetch(moveFileURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            is_private_file: selectValue === FileViewTypeEnum.PRIVATE
          })
        })

        if (response.ok) {
          const data = await response.json()
          console.log('File moved successfully:', data)

          // Update the file list with new CDN URL if provided
          const reducedFiles = fileList.reduce((reducer: fileInterface[], item, arrayIndex) => {
            if (arrayIndex === index) {
              return [
                ...reducer,
                {
                  ...currentFile,
                  fileViewType: selectValue,
                  isPrivateFile: !!(selectValue === FileViewTypeEnum.PRIVATE),
                  cloudLink: data?.newCdnUrl || currentFile.cloudLink // Update CDN URL if provided
                }
              ]
            }
            return [...reducer, { ...item }]
          }, [])

          setFileList(reducedFiles)
        } else {
          console.error('Failed to move file')
          toast({
            title: 'Error',
            description: 'Failed to change file visibility.',
            status: 'error',
            duration: 6000,
            isClosable: true
          })
        }
      } catch (error) {
        console.error('Error moving file:', error)
        toast({
          title: 'Error',
          description: 'Error changing file visibility.',
          status: 'error',
          duration: 6000,
          isClosable: true
        })
      }
    } else {
      // For new files without file_id, just update the local state
      const reducedFiles = fileList.reduce((reducer: fileInterface[], item, arrayIndex) => {
        if (arrayIndex === index) {
          return [
            ...reducer,
            {
              ...currentFile,
              fileViewType: selectValue,
              isPrivateFile: !!(selectValue === FileViewTypeEnum.PRIVATE)
            }
          ]
        }
        return [...reducer, { ...item }]
      }, [])

      setFileList(reducedFiles)
    }
  }

  return (
    <Box fontSize={'14px'}>
      <SystemFormHeading title="Upload Docs" />
      <SegmentBox>
        {fileList?.map((item, index) => (
          <Box
            sx={{ transition: '350ms' }}
            data-index={index}
            bg={dragEnterElement === index ? 'green.100' : 'transparent'}
            key={`fileupload_${index}`}
            mb="1"
            draggable="true"
            onDragOver={(e: React.DragEvent) => {
              if (dragEnterElement !== index) {
                setDragEnterElement(index)
              }
            }}
            onDragEnd={(e: React.DragEvent) => {
              setTimeout(() => {
                setDragEnterElement(null)
              }, 1500)
              const movement: dragMovementInterface = {
                dragged: index,
                dropped: dragEnterElement
              }
              const swappedArray = swapArrays(fileList, movement) as fileInterface[]

              updateFileListOrder(swappedArray)
            }}
          >
            <Box>
              <RadioGroup
                value={fileList[index].fileViewType}
                onChange={(selectValue: FileViewTypeEnum.PRIVATE | FileViewTypeEnum.PUBLIC) => {
                  void handleFileTypeChange(index, selectValue)
                }}
              >
                <Stack direction="row" spacing={4} mb="2">
                  <Radio value={FileViewTypeEnum.PRIVATE} colorScheme="green">
                    Private
                  </Radio>
                  <Radio value={FileViewTypeEnum.PUBLIC} colorScheme="green">
                    Public
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
            <Flex justifyContent={'center'} alignItems="flex-end">
              <Tag {...tagStyles} mb="20px">
                {index + 1}
              </Tag>
              <Box w="30%" ml="2%">
                <SnapSelectBox
                  title={index === 0 ? 'Document Type' : ''}
                  defaultValue={defaultFileType}
                  options={options}
                  chakraProps={{
                    value: fileList[index].fileType.id as string
                  }}
                  callBack={selectValue => {
                    const reducedFiles = fileList.reduce((reducer: fileInterface[], item, arrayIndex) => {
                      if (arrayIndex === index) {
                        return [
                          ...reducer,
                          {
                            ...fileList[index],
                            fileType: { id: selectValue.value }
                          }
                        ]
                      }

                      return [...reducer, { ...item }]
                    }, [])

                    setFileList(reducedFiles)
                  }}
                />
              </Box>
              <Box w="60%">
                <FormControl>
                  {index === 0 && (
                    <FormLabel fontWeight={'bold'} fontSize={'14px'}>
                      Choose File
                    </FormLabel>
                  )}
                  <Flex>
                    <Input
                      m="0"
                      mb="4"
                      type="file"
                      onChange={event => {
                        handleChange(event, index)
                      }}
                    />
                  </Flex>
                </FormControl>
              </Box>

              <Box ml="2" mb="4" display="flex" flexDir="column" gap="2">
                {item.product_doc_id && (
                  <Button
                    title={item.is_hero ? 'Hero photo' : 'Set as hero photo'}
                    size="xs"
                    variant="ghost"
                    p="1"
                    minW="6"
                    h="6"
                    onClick={async () => {
                      if (item.is_hero) return
                      try {
                        await setHeroPhoto({ variables: { product_doc_id: item.product_doc_id } })
                        setFileList(prev =>
                          prev.map((f, i) => ({ ...f, is_hero: i === index }))
                        )
                        toast({ title: 'Hero photo set', status: 'success', duration: 2000, isClosable: true })
                      } catch {
                        toast({ title: 'Failed to set hero photo', status: 'error', duration: 4000, isClosable: true })
                      }
                    }}
                  >
                    <Text fontSize="16px" color={item.is_hero ? '#861F41' : 'gray.400'}>
                      {item.is_hero ? '★' : '☆'}
                    </Text>
                  </Button>
                )}
                <Button
                  {...closeButtonStyles}
                  onClick={() => {
                    void handleFileDelete(index)
                  }}
                >
                  <CloseIcon boxSize={2} />
                </Button>
              </Box>
            </Flex>
            <CloudLink status={item.cloudLink} errorMessage={item.errorMessage} />
            {index < fileList.length - 1 && <Seperator />}
          </Box>
        ))}
      </SegmentBox>
      <Button
        {...addButtonStyles}
        onClick={() => {
          setFileList(rest => [
            ...rest,
            {
              file: null,
              fileType: { id: defaultFileType },
              md5: '',
              cloudLink: null,
              file_id: null,
              isPrivateFile: false,
              order: fileList.length,
              fileViewType: FileViewTypeEnum.PUBLIC
            }
          ])
        }}
      >
        <AddIcon boxSize={3} mr="2" /> Add File
      </Button>
    </Box>
  )
}
